import { useCallback } from 'react'
import {
  useGetMentionableUsersLazyQuery,
  useGetPreviewMentionableUsersLazyQuery,
} from '@sketch/gql-types'

interface ShareMentionableUsersParams {
  shareIdentifier: string
  previewIdentifier?: never
  previewPassword?: never
}

export interface PreviewMentionableUsersParams {
  previewIdentifier: string
  previewPassword?: string
  shareIdentifier?: never
}

export type MentionableUsersParams =
  | ShareMentionableUsersParams
  | PreviewMentionableUsersParams

const isPreviewMentionableUsersParams = (
  params: MentionableUsersParams
): params is PreviewMentionableUsersParams =>
  params.previewIdentifier !== undefined

const useMentionableUsers = (params: MentionableUsersParams) => {
  const isPreview = isPreviewMentionableUsersParams(params)

  const [
    queryShareMentionableUsers,
    { data: shareData, loading: shareLoading, called: shareCalled },
  ] = useGetMentionableUsersLazyQuery()

  const [
    queryPreviewMentionableUsers,
    { data: previewData, loading: previewLoading, called: previewCalled },
  ] = useGetPreviewMentionableUsersLazyQuery()

  const query = useCallback(() => {
    if (isPreviewMentionableUsersParams(params)) {
      return queryPreviewMentionableUsers({
        variables: {
          previewIdentifier: params.previewIdentifier,
          password: params.previewPassword,
        },
      })
    }

    return queryShareMentionableUsers({
      variables: { shareIdentifier: params.shareIdentifier },
    })
  }, [params, queryPreviewMentionableUsers, queryShareMentionableUsers])

  const users = isPreview
    ? previewData?.preview?.mentionableUsers || []
    : shareData?.share?.mentionableUsers || []
  const loading = isPreview ? previewLoading : shareLoading
  const called = isPreview ? previewCalled : shareCalled

  return [query, { loading, users, called }] as const
}

export default useMentionableUsers
